import * as SentrySDK from '@sentry/react';
import { CaptureConsole } from '@sentry/integrations';
import PrescriptionInterface from '../interfaces/prescription';
import { BuildInfoInterface, BuildAppInterface } from '../interfaces/build_info';
import { DrugstoreInterface } from '../interfaces/drugstore';
import { UserInterface } from '../interfaces/user';
import * as constants from '../services/constants';

interface BuildProps {
  buildApp: BuildAppInterface;
  buildInfo: BuildInfoInterface;
}

class Sentry {
  private static async getBuildInfos(): Promise<void | BuildProps> {
    // O arquivo "build_info.json" é injetado pela pipeline
    return fetch(`${constants.MEMED_URL}/modulos/plataforma.memed/branch/master/build_info.json`, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((buildInfo: BuildInfoInterface) => {
        const buildApp = buildInfo.applications.find((app: BuildAppInterface) => {
          return app.name === 'dispenser';
        });

        return {
          buildApp: buildApp as BuildAppInterface,
          buildInfo,
        };
      });
  }

  static setDrugstoreUser(user: UserInterface, drugstore?: DrugstoreInterface): void {
    const drugstoreTracking = {
      id: 'unavailable',
      email: 'unavailable',
      register_origin: 'unavailable',
      city: 'unavailable',
      uf: 'unavailable',
      chain_id: 'unavailable',
      drugstore_cnpj: 'unavailable',
      url: document.referrer ? document.referrer : window.location.origin,
    };

    if (user) {
      drugstoreTracking.id = user.id ? String(user.id) : 'unavailable';
      drugstoreTracking.email = user.email ? user.email : 'unavailable';
    }

    if (drugstore) {
      drugstoreTracking.register_origin = drugstore.register_origin ? drugstore.register_origin : 'unavailable';
      drugstoreTracking.city = drugstore.city ? drugstore.city : 'unavailable';
      drugstoreTracking.uf = drugstore.uf ? drugstore.uf : 'unavailable';
      drugstoreTracking.chain_id = drugstore.chain.id ? String(drugstore.chain.id) : 'unavailable';
      drugstoreTracking.drugstore_cnpj = drugstore.cnpj ? String(drugstore.cnpj) : 'unavailable';
    }

    SentrySDK.setUser(drugstoreTracking);
  }

  private static async setBuildTags(): Promise<void> {
    const buildTracking = {
      application: 'unavailable',
      build: 'unavailable',
      build_environment: 'unavailable',
      build_pipeline: 'unavailable',
      build_datetime: 'unavailable',
      build_app_hash: 'unavailable',
      build_app_tag: 'unavailable',
    };

    const buildInfos = await this.getBuildInfos();

    if (buildInfos) {
      buildTracking.application = buildInfos.buildApp.name ? buildInfos.buildApp.name : 'unavailable';
      buildTracking.build = buildInfos.buildInfo.build ? buildInfos.buildInfo.build : 'unavailable';
      buildTracking.build_environment = buildInfos.buildInfo.environment
        ? buildInfos.buildInfo.environment
        : 'unavailable';
      buildTracking.build_pipeline = buildInfos.buildInfo.pipeline ? buildInfos.buildInfo.pipeline : 'unavailable';
      buildTracking.build_datetime = buildInfos.buildInfo.datetime ? buildInfos.buildInfo.datetime : 'unavailable';
      buildTracking.build_app_hash = buildInfos.buildApp.hash ? buildInfos.buildApp.hash : 'unavailable';
      buildTracking.build_app_tag = buildInfos.buildApp.tag ? buildInfos.buildApp.tag : 'unavailable';
    }

    SentrySDK.setTags(buildTracking);
  }

  static setPrescriptionTags(prescription: PrescriptionInterface): void {
    const prescriptionTracking = {
      id: 'unavailable',
      token: 'unavailable',
      signed: 'unavailable',
      created_at: 'unavailable',
      doctor_id: 'unavailable',
      doctor_regional_code: 'unavailable',
      doctor_board_number: 'unavailable',
      doctor_city: 'unavailable',
      doctor_uf: 'unavailable',
    };

    if (prescription) {
      prescriptionTracking.id = prescription.id ? String(prescription.id) : 'unavailable';
      prescriptionTracking.token = prescription.token ? prescription.token : 'unavailable';
      prescriptionTracking.signed = String(prescription.signed);
      prescriptionTracking.created_at = prescription.created_at ? prescription.created_at : 'unavailable';
      prescriptionTracking.doctor_id = prescription.doctor.id ? String(prescription.doctor.id) : 'unavailable';
      prescriptionTracking.doctor_regional_code = prescription.doctor.regional_code
        ? prescription.doctor.regional_code
        : 'unavailable';
      prescriptionTracking.doctor_board_number = prescription.doctor.board_number
        ? prescription.doctor.board_number
        : 'unavailable';
      prescriptionTracking.doctor_city = prescription.doctor.city ? prescription.doctor.city : 'unavailable';
      prescriptionTracking.doctor_uf = prescription.doctor.uf ? prescription.doctor.uf : 'unavailable';
    }

    SentrySDK.setTags(prescriptionTracking);
  }

  static init(): void {
    if (constants.SENTRY_DSN) {
      SentrySDK.init({
        dsn: constants.SENTRY_DSN,
        integrations: [
          new CaptureConsole({
            // array of methods that should be captured
            // defaults to ['log', 'info', 'warn', 'error', 'debug', 'assert']
            levels: ['error'],
          }),
        ],
        environment: constants.NODE_ENV,
      });

      this.setBuildTags();
    }
  }
}

export default Sentry;
